// noinspection JSUnusedGlobalSymbols

import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {TranslateService} from '@ngx-translate/core';
import {map, switchMap} from 'rxjs/operators';
import { InlineNotificationService, PtCoreActions } from '@pt/pt-core';
import {of} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {Store} from '@ngrx/store';
import {
  DeleteDependencies,
  NotificationType,
  SessionInfoType,
  SessionNotification,
  SessionNotificationStatus,
  UiNotification
} from '@pt/pt-models';

import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import { ErrorDependencyDialogComponent } from '../dialogs/error-dependency-dialog.component';
import * as AiAssessmentActions from '../documents/store/actions/ai-assessment.actions';
import * as AppAssessmentActions from '../documents/store/actions/app-assessment.actions';
import * as CovidAssessmentActions from '../documents/store/actions/covid-assessment.actions';
import * as FolderActions from '../documents/store/actions/folder.actions';
import * as RiskAssessmentActions from '../documents/store/actions/risk-assessment.actions';
import * as TreatmentActions from '../documents/store/actions/treatment.actions';

//todo @Giovanni move to shared folder and change import on primatoo-ui and primatoo-ui-console
@Injectable()
export class AppNotifierEffects {

  constructor(
    private actions$: Actions,
    private translate: TranslateService,
    private toastr: ToastrService,
    private store: Store,
    private modalService: NgbModal,
    private inlineNotificationService: InlineNotificationService
  ) {
  }

  propagateNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.propagateNotification),
      map((action) => {
        if (!action.notification.translated) {
          this.store.dispatch(PtCoreActions.showNotification({
            notification: {
              type: action.notification.type,
              title: this.getTranslation(action.notification.title),
              message: this.getTranslation(action.notification.message)
            }
          }));
        } else {
          this.store.dispatch(PtCoreActions.showNotification({
            notification: {
              message: action.notification.message,
              type: action.notification.type,
              title: action.notification.title
            }
          }));
        }
      })
    ), {dispatch: false}
  );


  propagateDependenciesNotification$ = createEffect(() => this.actions$.pipe(
    ofType(PtCoreActions.propagateDependenciesNotification),
    map((action: { notification: DeleteDependencies }) => {
      const modalRef = this.modalService.open(ErrorDependencyDialogComponent, {
        centered: true,
        backdrop: 'static',
        size: 'lg'
      });
      modalRef.componentInstance.error = action.notification;
    })
  ), {dispatch: false});


  showNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.showNotification),
      map((action) => {
        //todo @georgy
        // condizione temporale mentre si migrano le notifiche
        // aggiunto regex per escludere le tabelle dove va bene la notifica
        const pathName = window.location.pathname;
        const rgx = new RegExp(/.*data-protection-governance\/.*\/.*|.*administration-configuration(\/.*\/.*|\/setting.*)/gm);
        if (rgx.test(pathName)) {
          this.inlineNotificationService.dispatchNotification(action.notification);
        } else {
          this.manageToast(action.notification, action.addToSession);
        }
      })
    ), {dispatch: false}
  );

  renameSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FolderActions.renameFolderSuccess,
        TreatmentActions.renameTreatmentSuccess,
        AiAssessmentActions.renameAiAssessmentSuccess,
        AppAssessmentActions.renameAppAssessmentSuccess,
        CovidAssessmentActions.renameCovidAssessmentSuccess,
        RiskAssessmentActions.renameRiskAssessmentSuccess,
      ),
      switchMap(() =>
        this.translate.stream('MESSAGE.SUCCESSFULLY_RENAMED').pipe(
          switchMap((message) => {
            return of(PtCoreActions.showNotification({
              notification: {
                type: NotificationType.success,
                title: null,
                message: message
              }
            }))
          })
        )
      )
    )
  );

  deleteFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FolderActions.deleteFolderFailed,
        TreatmentActions.deleteTreatmentFailed
      ),
      switchMap((action) =>
        of(PtCoreActions.showNotification({
          notification: {
            type: NotificationType.error,
            title: null,
            message: action.error
          }
        }))
      )
    )
  );

  showEditSuccessNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.showEditSuccess),
      switchMap(() => [
        PtCoreActions.showNotification({
          notification: {
            type: NotificationType.success,
            title: null,
            message: this.getTranslation('MESSAGE.EDIT_SUCCESS')
          },
          addToSession: false
        })
      ])
    )
  );
  showEditError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.showEditError),
      switchMap(() => [
        PtCoreActions.showNotification({
          notification: {
            type: NotificationType.error,
            title: null,
            message: this.getTranslation('MESSAGE.EDIT_ERROR')
          },
          addToSession: false
        })
      ])
    )
  );


  showOperationSuccessNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.showOperationSuccessNotification),
      switchMap(() => [
        PtCoreActions.showNotification({
          notification: {
            type: NotificationType.success,
            title: null,
            message: this.getTranslation('MESSAGE.OPERATION_SUCCESS')
          },
          addToSession: true
        })
      ])
    )
  );

  showOperationRunning$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.showOperationRunning),
      switchMap(() => [
        PtCoreActions.showNotification({
          notification: {
            type: NotificationType.success,
            title: null,
            message: this.getTranslation('MESSAGE.OPERATION_RUNNING')
          },
          addToSession: true
        })
      ])
    )
  );


  showOperationFailNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.showOperationFailNotification),
      switchMap(() => [
        PtCoreActions.showNotification({
          notification: {
            type: NotificationType.warning,
            title: null,
            message: this.getTranslation('MESSAGE.OPERATION_FAIL')
          },
          addToSession: true
        })
      ])
    )
  );

  private getTranslation(label: string) {
    let labelResponse = '';
    if (label !== null) {
      this.translate.stream(label).subscribe(translation => labelResponse = translation);
      return labelResponse;
    }
  }

  private manageToast(notification: UiNotification, addToSession: boolean) {
    let status: SessionNotificationStatus = SessionNotificationStatus.INFO;
    switch (notification.type) {
      case NotificationType.error:
        this.toastr.warning(notification.message, notification.title);
        status = SessionNotificationStatus.WARNING;
        break;
      case NotificationType.info:
        this.toastr.info(notification.message, notification.title);
        status = SessionNotificationStatus.INFO;
        break;
      case NotificationType.success:
        this.toastr.success(notification.message, notification.title);
        status = SessionNotificationStatus.SUCCESS;
        break;
      case NotificationType.warning:
        this.toastr.warning(notification.message, notification.title);
        status = SessionNotificationStatus.WARNING;
        break;
    }
    if (addToSession) {
      const sessionInfo: SessionNotification = {
        status,
        id: Date.now(),
        createdAt: Date.now(),
        name: notification.title,
        description: notification.message,
        actionType: SessionInfoType.NOTIFICATION
      };
      this.store.dispatch(PtCoreActions.addSessionNotifications({sessionNotification: sessionInfo}));
    }
  }
}
